import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTicket, processApproval } from '../utils/api';
import DecisionLogViewer from '../components/DecisionLogViewer';
import EscalationSummary from '../components/EscalationSummary';
import { getFaultCodeReferenceLinks } from '../utils/sourceRegistry';

function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorId, setSupervisorId] = useState('');

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  const loadTicket = async () => {
    try {
      const response = await getTicket(ticketId);
      setTicket(response.ticket);
      setLogs(response.decision_logs || []);
    } catch (err) {
      console.error('Failed to load ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (action) => {
    if (!supervisorName.trim() || !supervisorId.trim()) {
      alert('Supervisor name and ID are required for all approval actions.');
      return;
    }
    setProcessing(true);
    try {
      await processApproval(ticketId, supervisorId.trim(), supervisorName.trim(), action, approvalNotes);
      await loadTicket();
    } catch (err) {
      console.error('Approval failed:', err);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
        Loading ticket...
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="page-content">
        <p>Ticket not found.</p>
        <button className="btn btn--outline" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const riskClass = (ticket.triage_risk_level || 'medium').toLowerCase();
  const escalation = ticket.escalation_summary_json;
  const additionalInfo = (() => {
    const raw = ticket.additional_information_json;
    if (!raw) return [];
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return [];
    }
  })();

  const evidenceResults = (() => {
    const raw = ticket.evidence_results_json;
    if (!raw) return [];
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return [];
    }
  })();

  return (
    <div className="page-content" style={{ maxWidth: 800 }}>
      {/* Back button */}
      <button
        className="btn btn--outline btn--sm"
        onClick={() => navigate('/dashboard')}
        style={{ marginBottom: 20 }}
      >
        ← Back to Dashboard
      </button>

      {/* Ticket Header */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__header">
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--color-accent)' }}>
              {ticket.ticket_id}
            </div>
            <div className="card__title" style={{ marginTop: 4 }}>
              {ticket.equipment_model} — {ticket.fault_code}
            </div>
            <div className="card__subtitle">
              {ticket.equipment_type} · {ticket.model_year} · {ticket.operating_hours} hrs
            </div>
          </div>
          <span className={`badge badge--${riskClass}`}>
            {ticket.triage_risk_level || 'Unknown'}
          </span>
        </div>

        {/* Details */}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          <strong>Technician:</strong> {ticket.technician_name} ({ticket.technician_id})
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          <strong>Symptoms:</strong> {ticket.symptoms}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          <strong>Category:</strong> {(ticket.triage_category || '').replace('_', ' ')}
        </div>
        {ticket.fault_code && getFaultCodeReferenceLinks(ticket.fault_code).length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            <strong>Fault code reference:</strong>{' '}
            {getFaultCodeReferenceLinks(ticket.fault_code).map((link, idx) => (
              <span key={idx}>
                {idx > 0 && ' · '}
                <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
                  {link.name}
                </a>
              </span>
            ))}
          </div>
        )}
        {ticket.triage_confidence && (
          <div className="confidence-meter" style={{ marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Confidence</span>
            <div className="confidence-meter__bar">
              <div
                className="confidence-meter__fill"
                style={{
                  width: `${ticket.triage_confidence * 100}%`,
                  background: ticket.triage_confidence > 0.7
                    ? 'var(--color-safe)'
                    : ticket.triage_confidence > 0.5
                    ? 'var(--color-warning)'
                    : 'var(--color-danger)',
                }}
              />
            </div>
            <span className="confidence-meter__label">
              {(ticket.triage_confidence * 100).toFixed(0)}%
            </span>
          </div>
        )}

        {/* Reconciliation conflict — supervisor can resolve by choosing which triage to keep */}
        {ticket.reconciliation_conflict && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            background: 'var(--color-warning-bg)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 'var(--radius-sm)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 8 }}>
              Reconciliation Review
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
              This ticket was created offline. The on-device triage and the cloud AI triage produced different results. Please review and choose which classification to keep.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
              <div style={{ flex: 1, minWidth: 140, padding: 8, borderRadius: 'var(--radius-sm)', background: 'var(--neu-bg)', border: '1px solid var(--neu-shadow-dark)' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Cloud AI triage (current)</div>
                <div>Category: <strong>{(ticket.triage_category || '').replace(/_/g, ' ')}</strong></div>
                <div>Risk: <strong>{ticket.triage_risk_level}</strong></div>
                <div>Confidence: <strong>{ticket.triage_confidence ? `${(ticket.triage_confidence * 100).toFixed(0)}%` : 'N/A'}</strong></div>
              </div>
              <div style={{ flex: 1, minWidth: 140, padding: 8, borderRadius: 'var(--radius-sm)', background: 'var(--neu-bg)', border: '1px solid var(--neu-shadow-dark)' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Offline rules-based triage</div>
                <div>Category: <em>(based on fault code prefix mapping)</em></div>
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
              The cloud AI classification is applied by default. If you see issues, reject or request more info using the approval section below.
            </div>
          </div>
        )}
      </div>

      {/* Escalation Summary — same layout as field escalation window */}
      {escalation && (
        <div style={{ marginBottom: 24 }}>
          <EscalationSummary
            escalation={escalation}
            ticket={ticket}
            onNewTicket={() => {}}
            showNewTicketButton={false}
          />
        </div>
      )}

      {/* Evidence collected (steps + photos) */}
      {evidenceResults.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 className="card__title" style={{ marginBottom: 12 }}>Evidence collected</h3>
          {evidenceResults.map((step, idx) => (
            <div key={idx} className="evidence-collected-step">
              <div className="evidence-collected-step__header">
                <span className="evidence-collected-step__number">Step {step.step_number}</span>
                <span className="evidence-collected-step__action">{step.action}</span>
              </div>
              {step.result_value && (
                <div className="evidence-collected-step__notes">{step.result_value}</div>
              )}
              {step.photo_base64_list && step.photo_base64_list.length > 0 && (
                <div className="photo-thread" style={{ marginTop: 8 }}>
                  {step.photo_base64_list.map((dataUrl, i) => (
                    <img
                      key={i}
                      src={dataUrl}
                      alt={`Step ${step.step_number} photo ${i + 1}`}
                      className="photo-thread__img"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Q&A thread: supervisor questions and technician responses (additional information) */}
      {additionalInfo.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 className="card__title" style={{ marginBottom: 12 }}>Additional information (Q&A)</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {additionalInfo.map((entry, idx) => (
              <li
                key={idx}
                style={{
                  marginBottom: 12,
                  padding: 12,
                  borderRadius: 'var(--radius-sm)',
                  background: entry.from === 'supervisor' ? 'var(--color-info-bg, #f0f7ff)' : 'var(--color-safe-bg, #f0fff4)',
                  borderLeft: `4px solid ${entry.from === 'supervisor' ? 'var(--color-accent)' : 'var(--color-safe)'}`,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {entry.from === 'supervisor' ? 'Supervisor' : 'Technician'} · {entry.at ? new Date(entry.at).toLocaleString() : ''}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{entry.message}</div>
                {entry.photos && entry.photos.length > 0 && (
                  <div className="photo-thread">
                    {entry.photos.map((dataUrl, i) => (
                      <img key={i} src={dataUrl} alt={`Attachment ${i + 1}`} className="photo-thread__img" />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Technician's response when supervisor requested more info (legacy single response) */}
      {ticket.more_info_response && additionalInfo.length === 0 && (
        <div className="card escalation-summary" style={{ marginBottom: 16 }}>
          <h3 className="escalation-summary__title">Technician response (more info)</h3>
          <div className="escalation-summary__summary-box" style={{ whiteSpace: 'pre-wrap' }}>
            {ticket.more_info_response}
          </div>
        </div>
      )}

      {/* Approval Actions — only when ticket is in queue (escalated); after tech submits more info it returns to escalated */}
      {ticket.status === 'escalated' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__title" style={{ marginBottom: 12 }}>Supervisor Action</div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <label className="form-label">Your name *</label>
              <input
                className="form-input"
                type="text"
                value={supervisorName}
                onChange={(e) => setSupervisorName(e.target.value)}
                placeholder="e.g. Maria Chen"
              />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 120, marginBottom: 0 }}>
              <label className="form-label">Supervisor ID *</label>
              <input
                className="form-input"
                type="text"
                value={supervisorId}
                onChange={(e) => setSupervisorId(e.target.value)}
                placeholder="e.g. SUP-0042"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes (required when requesting more info)</label>
            <textarea
              className="form-textarea"
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Approve/Reject: optional notes. Request More Info: type your question or message for the technician..."
              rows={3}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn btn--approve"
              disabled={processing || !supervisorName.trim() || !supervisorId.trim()}
              onClick={() => handleApproval('approved')}
            >
              {processing ? 'Processing...' : 'Approve'}
            </button>
            <button
              className="btn btn--reject"
              disabled={processing || !supervisorName.trim() || !supervisorId.trim()}
              onClick={() => handleApproval('rejected')}
            >
              Reject
            </button>
            <button
              className="btn btn--outline"
              disabled={processing || !supervisorName.trim() || !supervisorId.trim() || (approvalNotes.trim() === '')}
              onClick={() => handleApproval('more_info_requested')}
              title={approvalNotes.trim() === '' ? 'Enter a message or question for the technician first' : 'Send request for more information'}
            >
              Request More Info
            </button>
          </div>
          {(!supervisorName.trim() || !supervisorId.trim()) && (
            <p style={{ fontSize: 12, color: 'var(--color-warning)', marginTop: 8, fontWeight: 600 }}>
              A named human approver is required for all safety, warranty, and billing decisions.
            </p>
          )}
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            When you request more info, your message is sent to the technician. They can reply and attach photos; their response will appear here and the ticket will return to your queue.
          </p>
        </div>
      )}

      {/* Approval result display */}
      {ticket.approved_by && (
        <div className="card" style={{
          marginBottom: 16,
          borderColor: ticket.approval_action === 'approved' ? 'var(--color-safe)' : 'var(--color-danger)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {ticket.approval_action === 'approved' ? '✓ Approved' : ticket.approval_action === 'rejected' ? '✗ Rejected' : '? More Info Requested'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            By {ticket.approved_by} at {ticket.approved_at}
          </div>
          {ticket.approval_notes && (
            <div style={{ fontSize: 13, marginTop: 8 }}>{ticket.approval_notes}</div>
          )}
        </div>
      )}

      {/* Decision Log Viewer (Detailed) */}
      <div className="card">
        <div className="card__title" style={{ marginBottom: 16 }}>Decision Log & Reasoning</div>
        <DecisionLogViewer logs={logs} />
      </div>
    </div>
  );
}

export default TicketDetail;
